import React, { useMemo } from 'react';

import classNames from 'classnames';

import { useProcurement } from '../context/ProcurementContext';

import { COVERAGE_COLOURS, money } from './overviewHelpers';

/**
 * IB-699 — "Coverage by department": per-department stacked bars (committed / in procurement
 * plan / still-to-plan) against each department's budget. Departments with budget but no
 * procurement records still appear (fully still-to-plan) — that gap is the point of the chart.
 *
 * The department name links to the IB-702 department procurement view when that route is
 * available; until IB-702 is live the route is absent, so the name renders non-navigating.
 */
const DepartmentRow = ({ department, maxBudget, currencySymbol, departmentUrl }) => {
    const budget = Number(department.budget) || 0;
    const committed = Number(department.committed) || 0;
    const inPlan = Number(department.in_plan) || 0;
    const stillToPlan = Number(department.still_to_plan) || 0;

    const outerWidth = maxBudget > 0 ? (budget / maxBudget) * 100 : 0;
    const segPct = (value) => (budget > 0 ? (value / budget) * 100 : 0);

    const segments = [
        { key: 'committed', colour: COVERAGE_COLOURS.committed, value: committed },
        { key: 'inPlan', colour: COVERAGE_COLOURS.inPlan, value: inPlan },
        { key: 'stillToPlan', colour: COVERAGE_COLOURS.stillToPlan, value: stillToPlan },
    ];

    const href = departmentUrl && department.department_id ? departmentUrl.replace('DEPARTMENT_ID', department.department_id) : null;
    const NameTag = href ? 'a' : 'span';

    return (
        <div className="tw-flex tw-items-center tw-gap-4 tw-py-2">
            <NameTag
                href={href || undefined}
                data-action={href ? 'navigate' : undefined}
                className={classNames('tw-w-48 tw-shrink-0 tw-text-sm tw-text-charcoal tw-truncate', href && 'hover:tw-text-blue hover:tw-underline')}
            >
                {department.department_name || $t('Unassigned')}
            </NameTag>

            <div className="tw-flex-1">
                <div className="tw-h-4 tw-w-full">
                    <div className="tw-flex tw-h-full tw-rounded-md tw-overflow-hidden tw-bg-grey" style={{ width: `${outerWidth}%` }}>
                        {segments.map((segment) => (
                            <div key={segment.key} className={segment.colour} style={{ width: `${segPct(segment.value)}%` }} />
                        ))}
                    </div>
                </div>
            </div>

            <div className="tw-w-28 tw-shrink-0 tw-text-end">
                <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{money(currencySymbol, budget)}</div>
                {stillToPlan > 0 ? (
                    <div className="tw-text-xs tw-text-charcoal-50">{`${money(currencySymbol, stillToPlan)} ${$t('to plan')}`}</div>
                ) : (
                    <div className="tw-text-xs tw-font-medium tw-text-green">{$t('fully planned')}</div>
                )}
            </div>
        </div>
    );
};

const LegendItem = ({ colour, label }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <span className={classNames('tw-h-3 tw-w-3 tw-rounded-sm tw-shrink-0', colour)} />
        <span className="tw-text-sm tw-text-charcoal-75">{label}</span>
    </div>
);

const CoverageByDepartment = ({ stats, currencySymbol }) => {
    const { routes } = useProcurement();
    const departments = stats?.by_department ?? [];

    // Order by budget descending (largest departments first), matching the mockup.
    const sorted = useMemo(() => [...departments].sort((a, b) => (Number(b.budget) || 0) - (Number(a.budget) || 0)), [departments]);
    const maxBudget = useMemo(() => Math.max(1, ...sorted.map((department) => Number(department.budget) || 0)), [sorted]);

    if (sorted.length === 0) {
        return null;
    }

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-flex tw-items-baseline tw-gap-2 tw-mb-4">
                <h4 className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Coverage by department')}</h4>
                <span className="tw-text-sm tw-text-charcoal-50">{$t('committed · in procurement plan · still to be planned, against budget')}</span>
            </div>

            <div className="tw-divide-y tw-divide-grey">
                {sorted.map((department) => (
                    <DepartmentRow
                        key={department.department_id}
                        department={department}
                        maxBudget={maxBudget}
                        currencySymbol={currencySymbol}
                        departmentUrl={routes.departmentProcurementUrl}
                    />
                ))}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-mt-4">
                <LegendItem colour={COVERAGE_COLOURS.committed} label={$t('Committed')} />
                <LegendItem colour={COVERAGE_COLOURS.inPlan} label={$t('In procurement plan')} />
                <LegendItem colour={COVERAGE_COLOURS.stillToPlan} label={$t('Still to be planned')} />
            </div>
        </div>
    );
};

export default CoverageByDepartment;
